import type { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";

/**
 * Integration setup guides (/integrations/<provider>) — the named-tool
 * specificity that both searchers ("mailgun smtp crm") and AI assistants
 * need. Values below are the providers' stable, documented settings;
 * every email guide points at SPF/DKIM because that's what actually
 * decides deliverability.
 */

type Integration = {
  slug: string;
  name: string;
  kind: "SMTP (email)" | "SMS" | "Webhooks";
  tagline: string;
  settings?: [string, string][];
  steps: string[];
  faq: { q: string; a: string }[];
};

export const INTEGRATIONS: Integration[] = [
  {
    slug: "mailgun",
    name: "Mailgun",
    kind: "SMTP (email)",
    tagline: "Developer-friendly sending with strong EU/US region support.",
    settings: [
      ["SMTP host", "smtp.mailgun.org"],
      ["Port", "587 (STARTTLS)"],
      ["Username", "postmaster@mg.yourdomain.com"],
      ["Password", "your Mailgun SMTP password"],
    ],
    steps: [
      "In Mailgun: Sending → Domains → add your domain (a subdomain like mg.yourdomain.com keeps root-domain DNS untouched).",
      "Add the DNS records Mailgun shows you — SPF and DKIM are the ones deliverability lives or dies on. Wait for verification.",
      "Grab the SMTP credentials from Domain settings → SMTP credentials.",
      "In FrazzleCRM: Settings → Credentials → Add SMTP — paste host, port, username, password, and set your From name/email.",
      "Send yourself a test from the credential card, then pick this credential on any email step (or set it as the default).",
    ],
    faq: [
      { q: "Does FrazzleCRM charge for emails sent through Mailgun?", a: "No. You pay Mailgun directly at their rates; FrazzleCRM adds no per-email markup on any plan." },
      { q: "Flex or Foundation plan?", a: "Mailgun's pay-as-you-go Flex tier is fine to start; move up when volume or dedicated-IP needs grow." },
    ],
  },
  {
    slug: "amazon-ses",
    name: "Amazon SES",
    kind: "SMTP (email)",
    tagline: "The cheapest serious sending on the internet (~$0.10 per 1,000).",
    settings: [
      ["SMTP host", "email-smtp.us-east-1.amazonaws.com (use your region)"],
      ["Port", "587 (STARTTLS)"],
      ["Username / password", "SES SMTP credentials (created in the SES console)"],
    ],
    steps: [
      "In the SES console: Verified identities → verify your domain, and add the DKIM records SES generates.",
      "Create SMTP credentials (SES console → SMTP settings → Create SMTP credentials — these are NOT your AWS keys).",
      "New AWS accounts start in sandbox mode: request production access (a one-form support request, usually approved in a day).",
      "In FrazzleCRM: Settings → Credentials → Add SMTP with your region's host, port 587 and the SMTP credentials.",
      "Test-send, then select the credential on your email steps.",
    ],
    faq: [
      { q: "Why is SES so cheap?", a: "It's raw infrastructure: ~$0.10 per 1,000 emails, but you bring your own monitoring and warm-up discipline. FrazzleCRM's broken-link watchdog and your own bounce handling cover part of that; SES reputation dashboards cover the rest." },
      { q: "Which region should I pick?", a: "The one closest to you operationally — just make sure the SMTP host region matches where you verified the domain." },
    ],
  },
  {
    slug: "sendgrid",
    name: "SendGrid",
    kind: "SMTP (email)",
    tagline: "Ubiquitous, generous free tier, five-minute setup.",
    settings: [
      ["SMTP host", "smtp.sendgrid.net"],
      ["Port", "587 (STARTTLS)"],
      ["Username", "apikey (literally the word)"],
      ["Password", "your SendGrid API key"],
    ],
    steps: [
      "In SendGrid: Settings → Sender Authentication → authenticate your domain (adds SPF/DKIM CNAMEs).",
      "Create an API key with Mail Send permission (Settings → API Keys).",
      "In FrazzleCRM: Settings → Credentials → Add SMTP — host smtp.sendgrid.net, port 587, username apikey, password = the API key.",
      "Test-send and assign to your email steps.",
    ],
    faq: [
      { q: "The username is really just \"apikey\"?", a: "Yes — SendGrid's SMTP auth uses the literal string apikey as the username and your API key as the password. It surprises everyone once." },
    ],
  },
  {
    slug: "postmark",
    name: "Postmark",
    kind: "SMTP (email)",
    tagline: "The deliverability nerds' choice for transactional email.",
    settings: [
      ["SMTP host", "smtp.postmarkapp.com"],
      ["Port", "587 (STARTTLS)"],
      ["Username / password", "your Server API token (both fields)"],
    ],
    steps: [
      "In Postmark: create a Server, then Sender Signatures → add and DKIM-verify your domain.",
      "Copy the Server API token — it's both SMTP username and password.",
      "Postmark separates transactional and broadcast Message Streams; pick the stream that matches what your automation sends.",
      "In FrazzleCRM: Settings → Credentials → Add SMTP with the values above; test-send.",
    ],
    faq: [
      { q: "Can I send marketing email through Postmark?", a: "Yes, via their Broadcast message streams — keep promotional sequences on a broadcast stream so their famously-clean transactional IPs stay that way." },
    ],
  },
  {
    slug: "brevo",
    name: "Brevo",
    kind: "SMTP (email)",
    tagline: "Straightforward European option with a friendly free tier.",
    settings: [
      ["SMTP host", "smtp-relay.brevo.com"],
      ["Port", "587 (STARTTLS)"],
      ["Username", "your Brevo account email"],
      ["Password", "your SMTP key (not your account password)"],
    ],
    steps: [
      "In Brevo: Senders & Domains → authenticate your domain (SPF/DKIM records).",
      "SMTP & API → generate an SMTP key.",
      "In FrazzleCRM: Settings → Credentials → Add SMTP with the values above; test-send and assign.",
    ],
    faq: [
      { q: "Is the free tier enough to start?", a: "Brevo's free tier has a daily send cap — fine for testing and small lists; upgrade when a real sequence goes live." },
    ],
  },
  {
    slug: "twilio",
    name: "Twilio",
    kind: "SMS",
    tagline: "SMS steps, appointment reminders and no-show killers.",
    settings: [
      ["Account SID", "from the Twilio console dashboard"],
      ["Auth token", "from the same dashboard"],
      ["From number", "an SMS-capable Twilio number you own"],
    ],
    steps: [
      "Buy an SMS-capable number in the Twilio console (Phone Numbers → Buy a number).",
      "US sending: register for A2P 10DLC (Messaging → Regulatory compliance) — carriers filter unregistered traffic hard.",
      "In FrazzleCRM: Settings → Credentials → Add Twilio — paste Account SID, auth token and the From number.",
      "Drop a Send SMS step on any flow and pick the credential. Reminder templates (24h/1h) are in the No-Show Killer template.",
    ],
    faq: [
      { q: "What does an SMS cost?", a: "You pay Twilio directly — roughly a cent per segment in the US. FrazzleCRM adds no per-message markup on any plan." },
      { q: "Do I need 10DLC registration?", a: "For US traffic, effectively yes — unregistered long-code SMS gets filtered aggressively. It's a one-time registration through the Twilio console." },
    ],
  },
  {
    slug: "webhooks",
    name: "Webhooks (Stripe, Shopify, Cal.com, anything)",
    kind: "Webhooks",
    tagline: "Any app that can POST JSON can trigger your funnels.",
    steps: [
      "In FrazzleCRM, drop an Inbound Webhook node on your map — it mints a live URL (/api/wh/<id>) the moment it lands.",
      "Point the source app at that URL: Stripe → Developers → Webhooks (e.g. checkout.session.completed); Shopify → Settings → Notifications → Webhooks (e.g. orders/create, checkouts/create); Cal.com → Developer → Webhooks (booking created).",
      "Every field of the posted JSON is available as {{webhook.*}} merge fields in your emails and SMS.",
      "Wire the webhook node into a flow — enrollment, tagging, sequences — and watch test events arrive in the run log.",
    ],
    faq: [
      { q: "Does FrazzleCRM have native Stripe/Shopify apps?", a: "Not yet — integration is via webhooks, which both platforms support natively and which takes about 15 minutes to wire. The upside: any tool that can send a webhook integrates the same way, no marketplace required." },
      { q: "Can events create contacts automatically?", a: "Yes — automations triggered by a webhook can upsert the contact (deduped by email or phone) before running the rest of the flow." },
    ],
  },
];

type Props = { integ: Integration };

export const getStaticPaths: GetStaticPaths = () => ({
  paths: INTEGRATIONS.map((i) => ({ params: { slug: i.slug } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<Props> = ({ params }) => {
  const integ = INTEGRATIONS.find((i) => i.slug === String(params?.slug));
  return integ ? { props: { integ } } : { notFound: true };
};

export default function IntegrationPage({ integ }: Props) {
  return (
    <SiteShell
      title={`Connect ${integ.name} to FrazzleCRM — setup guide`}
      description={`How to connect ${integ.name} to FrazzleCRM (${integ.kind.toLowerCase()}): exact settings, step-by-step setup, and what it costs. ${integ.tagline}`}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: integ.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          }}
        />
      </Head>

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-4xl px-6 pb-10 pt-14">
          <Link href="/integrations" className="text-[12.5px] font-medium text-slate-400 hover:text-slate-600">
            ← All integrations
          </Link>
          <div className="mt-3 flex items-center gap-3">
            <h1 className="text-[32px] font-extrabold tracking-tight text-slate-900">
              FrazzleCRM + {integ.name}
            </h1>
            <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-0.5 text-[11px] font-semibold text-indigo-700">
              {integ.kind}
            </span>
          </div>
          <p className="mt-2 max-w-xl text-[15px] text-slate-600">{integ.tagline}</p>
        </div>
      </section>

      {integ.settings && (
        <section className="mx-auto max-w-4xl px-6 pt-8">
          <h2 className="text-[18px] font-bold text-slate-900">The settings</h2>
          <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
            {integ.settings.map(([k, v]) => (
              <div key={k} className="flex border-b border-slate-100 text-[13.5px] last:border-0">
                <span className="w-44 shrink-0 bg-slate-50/80 px-4 py-2.5 font-medium text-slate-600">{k}</span>
                <span className="px-4 py-2.5 font-mono text-[12.5px] text-slate-800">{v}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-4xl px-6 py-8">
        <h2 className="text-[18px] font-bold text-slate-900">Setup, step by step</h2>
        <ol className="mt-4 space-y-3">
          {integ.steps.map((s, i) => (
            <li key={s} className="flex gap-3 rounded-2xl border border-slate-100 bg-white p-4 text-[14px] leading-relaxed text-slate-700 shadow-sm">
              <span className="shrink-0 font-bold text-indigo-500">{i + 1}.</span>
              {s}
            </li>
          ))}
        </ol>
        {integ.kind === "SMTP (email)" && (
          <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-800">
            <strong>Deliverability in one line:</strong> SPF + DKIM on your
            domain, warm up gradually, and let FrazzleCRM&apos;s broken-link
            watchdog catch dead links before subscribers do.
          </p>
        )}
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-10">
        <h2 className="text-[18px] font-bold text-slate-900">Common questions</h2>
        <div className="mt-4 space-y-4">
          {integ.faq.map((f) => (
            <div key={f.q} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <h3 className="text-[14.5px] font-bold text-slate-900">{f.q}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">{f.a}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <a
            href={`${APP_URL}/signup`}
            className="inline-block rounded-xl bg-indigo-600 px-7 py-3 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
          >
            Start free and connect {integ.name}
          </a>
        </div>
      </section>
    </SiteShell>
  );
}
